import { Directive, ElementRef, HostBinding, inject, Input } from '@angular/core';
import { Store } from '@ngrx/store';

import { SLOT_MINIMAL_WIDTH, SlotAlignment, SlotId } from '@blaze-runtime/engine-domain';
import {
  BLAZE_SLOT_CLASS,
  BLAZE_TARGET_CLASS,
  ELEMENT_ID_ATTR,
  ELEMENT_SLOT_CONTAINER_ATTR,
  type SlotNodeData,
} from '@blaze-runtime/engine-domain';
import { RuntimeMode } from '@blaze-shared/app-state-data-access';
import { type RenderElementDataRowContext, type ViewportMode } from '@blaze-shared/elements-domain';
import { LEFT_MOUSE_BUTTON } from '@blaze-shared/shared-util';

import { elementMouseDown } from '../../+state/canvas';
import { addEventListenerForRuntimeMode } from '../../utils/runtime';
import { arePropsWithVisibility } from '../element-model/are-props-with-visibility';
import { getVerticalAlignment } from '../element-model/slots';

// TODO: test
@Directive({
  selector: '[blazeSlotNode]',
})
export class SlotNodeDirective {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly store = inject(Store);

  @Input() blazeSlotNode?: SlotNodeData;
  @Input() blazeSlotNodeRowContext?: RenderElementDataRowContext;
  @Input() blazeSlotNodeForceExpand?: boolean;
  @Input() viewportMode?: ViewportMode;

  @HostBinding(`attr.${ELEMENT_SLOT_CONTAINER_ATTR}`) get slotId(): SlotId | undefined {
    return this.blazeSlotNode?.slotId;
  }
  @HostBinding(`attr.${ELEMENT_ID_ATTR}`) get elementId(): string | undefined {
    return this.blazeSlotNode?.elementId;
  }

  @HostBinding('class.selected') get isSelected(): boolean | undefined {
    return this.blazeSlotNode?.isSelected;
  }
  @HostBinding('class.on-path') get isOnPath(): boolean | undefined {
    return this.blazeSlotNode?.isOnPath;
  }
  @HostBinding('class.hosted') get isHosted(): boolean {
    return this.getIsHosted();
  }
  @HostBinding('class.sync-vertical-line-alignment') get syncVerticalLineAlignment():
    | boolean
    | undefined {
    return this.blazeSlotNode?.alignment?.syncVerticalLineAlignment;
  }
  @HostBinding('class.force-expand') get forceExpand(): boolean | undefined {
    return this.blazeSlotNodeForceExpand;
  }
  @HostBinding('class.be-hidden') get isViewportHidden(): boolean | undefined {
    return this.getIsViewportHidden();
  }
  @HostBinding(`style.align-items`) get alignItems(): string {
    return this.getAlignItems;
  }
  @HostBinding(`style.vertical-align`) get verticalAlign(): SlotAlignment['vertical'] | undefined {
    return this.getVerticalLineAlignment;
  }
  @HostBinding(`style.text-align`) get textAlign(): string {
    return this.blazeSlotNode?.alignment?.horizontal ?? 'left';
  }
  @HostBinding(`style.flex`) get styleFlex(): string | undefined {
    return this.getSlotFlex;
  }

  /**
   * Vertical alignment
   */
  private get getAlignItems(): 'flex-start' | 'center' | 'flex-end' | 'stretch' {
    // If slot works as a host to another layout (example: navContainer), the 'stretch' alignment should be used,
    // so the root element of the hosted layout uses the whole provided space even when there are no elements inside of it.
    // This makes it possible to place first elements inside of the hosted layout.
    if (this.getIsHosted()) {
      return 'stretch';
    }

    switch (this.blazeSlotNode?.alignment?.vertical) {
      case 'bottom':
        return 'flex-end';
      case 'middle':
        return 'center';
      case 'top':
      default:
        return 'flex-start';
    }
  }
  /**
   * Get column size (for adjustable column layout)
   */
  private get getSlotFlex(): string | undefined {
    if (this.blazeSlotNode?.isColumn === false) {
      return;
    }

    const sizing = this.blazeSlotNode?.sizing;

    if (sizing?.mode === 'percent') {
      // grow minimally just to fill 100% width of the container if columns collapse, but don't shrink
      return `1 0 ${sizing.width}%`;
    } else if (sizing?.mode === 'fit-content') {
      return `0 0 ${SLOT_MINIMAL_WIDTH}`; // don't grow, don't shrink, use 16px basis as a minimal column width
    } else {
      // Flex grow is enormous here, so it uses all available space, even if there is a 'percent' column in the same line
      return `1e10 1 ${SLOT_MINIMAL_WIDTH}`; // grow and shrink automatically with minimal width
    }
  }

  private get getVerticalLineAlignment(): SlotAlignment['vertical'] | undefined {
    const alignment = this.blazeSlotNode?.alignment;

    if (!alignment) {
      return getVerticalAlignment();
    }

    return alignment.syncVerticalLineAlignment
      ? getVerticalAlignment(this.blazeSlotNode?.alignment)
      : undefined;
  }
  constructor() {
    this.elementRef.nativeElement.classList.add(BLAZE_TARGET_CLASS);
    this.elementRef.nativeElement.classList.add(BLAZE_SLOT_CLASS);
    this.elementRef.nativeElement.style.display = 'flex';
    this.elementRef.nativeElement.style.position = 'relative';

    addEventListenerForRuntimeMode(
      'mousedown',
      this.onElementMouseDown.bind(this),
      this.elementRef.nativeElement,
      RuntimeMode.Editor,
    );
  }

  private getIsHosted(): boolean {
    return this.blazeSlotNode?.hosting?.ns !== undefined;
  }

  private onElementMouseDown(mouseDownEvent: MouseEvent): void {
    if (
      mouseDownEvent.button !== LEFT_MOUSE_BUTTON ||
      this.blazeSlotNode?.elementId === undefined
    ) {
      return;
    }

    mouseDownEvent.preventDefault();
    mouseDownEvent.stopPropagation();

    this.store.dispatch(
      elementMouseDown({
        mouseDownEvent,
        elementId: this.blazeSlotNode.elementId,
        slotId: this.blazeSlotNode.slotId,
        rowContext: this.blazeSlotNodeRowContext,
      }),
    );
  }

  private getIsViewportHidden(): boolean {
    if (!this.viewportMode || !arePropsWithVisibility(this.blazeSlotNode?.props)) {
      return false;
    }

    const viewports = this.blazeSlotNode?.props?.content?.visible?.viewports;

    if (!viewports?.length) {
      return false;
    }

    return !viewports.includes(this.viewportMode);
  }
}
